import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, usePathname, useRouter } from '@framework/next/navigation';
import cn from 'classnames';
import noop from 'lodash/noop';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import replaceUrlTail from '@framework/data-helpers/url/replaceUrlTail';
import AccessRequestModal from '../Modals/AccessRequestModal';
import ExploreTableZeroCase from './ExploreTableZeroCase';
import DataroomsList from '../../Datarooms/DataroomsList';
import Input from '@dealroadshow/uikit-input';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import {
  FILTER_BY_NAME_SECTOR_PLACEHOLDER,
  FILTER_BY_NAME_SECTOR_SPONSOR_PLACEHOLDER,
} from '../../common/constants';
import Column from '../../common/Column';
import TransactionTypeTabs from '../../Datarooms/TransactionTypeTabs';
import { useRestrictedListingContext } from '../../../contexts/RestrictedListingContext';
import { useTransactionTypeGroupsContext } from '../../../contexts/TransactionTypeGroupsContext';
import cardStyles from '@dealroadshow/uikit-styles/card.module.scss';
import spacesStyles from '@dealroadshow/uikit-styles/helpers/spaces.module.scss';
import styles from '../../Datarooms/datarooms.module.scss';
import IconFiltering from '@dealroadshow/uikit-icon/IconFiltering';
import config from "@dataroom/config";

export interface IExploreSetsProps {
  zeroCaseComponent?: React.ReactNode,
  onPageChange?: () => void,
  columns?: Array<Column>,
}

const ExploreSetsTable = ({
  zeroCaseComponent,
  onPageChange = noop,
  columns = [
    Column.Sector,
    Column.NameNotClickable,
    Column.Sponsor,
    Column.Approved,
    Column.Files,
    Column.Size,
    Column.LastActivityExploreSets,
  ],
}: IExploreSetsProps) => {
  const [searchStringValue, setSearchStringValue] = useState('');
  const scrollRef = useRef(null);
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const activeGroup = params?.transactionTypeGroupName?.toString();
  const { tenant } = useDataroomTenantContext();
  const isVerisendOrResearchRoom = (
    tenant === config.verisend.code ||
    tenant === config.researchroom.code
  );
  const isRestrictionByCountryEnabled = config[tenant].options;
  const searchString = searchParams.get('search');

  if (isVerisendOrResearchRoom) {
    columns = columns.map((item) => {
      if (item === Column.Sponsor) return Column.Underwriter;
      if (isRestrictionByCountryEnabled && item === Column.LastActivityExploreSets) {
        return Column.LastActivityExploreSetsWithCountryCheckCallback;
      }
      return item;
    });
  }

  const {
    isFetching,
    isZeroCase,
    isInitialized,
    collection,
    totalCount,
    page,
    perPage,
    sortBy,
    sortOrder,
    debouncedSearch,
    sort,
    paginate,
    setItemsPerPage,
    getCollection,
  } = useRestrictedListingContext();

  const isInitialDataFetching = isFetching && !isInitialized;
  const isCustomZeroCase = isZeroCase && !!zeroCaseComponent;

  const {
    collection: transactionTypeGroupsCollection,
    defaultGroup,
    setActiveGroup,
  } = useTransactionTypeGroupsContext();

  useEffect(() => {
    if (searchString) {
      setSearchStringValue(searchString);
      debouncedSearch(searchString);
    }
  }, [searchString]);

  useEffect(() => {
    if (!defaultGroup) {
      return;
    }

    const transactionTypeGroup = transactionTypeGroupsCollection.find(
      (item) => item.name.toLowerCase() === activeGroup,
    );

    if (!transactionTypeGroup) {
      setActiveGroup(defaultGroup);
      router.replace(replaceUrlTail(pathname, activeGroup!, defaultGroup.name.toLowerCase()));
    } else {
      setActiveGroup(transactionTypeGroup);
    }
  }, [activeGroup, defaultGroup]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchStringValue(value);
    debouncedSearch(value);
  };

  const handlePageChange = (page: any) => {
    paginate(page);
    onPageChange();
  };

  return (
    <>
      { !isCustomZeroCase && (
        <div
          className={ cn(cardStyles.cardContainer, styles.card) }
          ref={ scrollRef }
          data-test={ `${ tenant }DataroomTable` }
        >
          { !!transactionTypeGroupsCollection.length && defaultGroup && (
            <div
              className={ cn(
                styles.cardInnerHorizontal,
                spacesStyles.pbn,
                { [styles.tabsLine]: !!transactionTypeGroupsCollection.length },
              ) }
            >
              <div className={ cardStyles.cardHeader }>
                <div className={ cn(cardStyles.cardHeaderActions, styles.searchBlock) }>
                  <div
                    className={ styles.searchInputFixedWidth }
                    data-test={ `${ tenant }SearchTable` }
                  >
                    { /* @ts-ignore */ }
                    <Input
                      name="search"
                      className={ styles.searchInputFormField }
                      inputClassName={ styles.searchInputDisabled }
                      icon={ IconFiltering }
                      value={ searchStringValue }
                      placeholder={
                        isVerisendOrResearchRoom
                          ? FILTER_BY_NAME_SECTOR_PLACEHOLDER
                          : FILTER_BY_NAME_SECTOR_SPONSOR_PLACEHOLDER
                      }
                      onChange={ handleSearchChange }
                      isNarrow
                      dataTest="exploreSetsInput"
                      disabled={ isZeroCase }
                    />
                  </div>
                </div>
                <TransactionTypeTabs
                  searchString={ searchStringValue }
                  disabled={ isZeroCase }
                />
              </div>
            </div>
          ) }
          { isInitialDataFetching && (
            <div className={ styles.initialPreloaderWrp }>
              <Spinner />
            </div>
          ) }
          { !isInitialDataFetching && !isZeroCase && (
            <DataroomsList
              tenant={ tenant }
              columns={ columns }
              collection={ collection }
              isFetching={ isFetching }
              sortBy={ sortBy as string }
              sortOrder={ sortOrder! }
              totalCount={ totalCount }
              page={ page as number }
              perPage={ perPage as number }
              scrollRef={ scrollRef as unknown as React.RefObject<HTMLElement> }
              paginate={ handlePageChange }
              setItemsPerPage={ setItemsPerPage }
              sort={ sort as unknown as (sortBy: string, sortOrder: string) => void }
              isExploreSetsTab
            />
          ) }
          { isZeroCase && (<ExploreTableZeroCase />) }
        </div>
      ) }
      { isCustomZeroCase && zeroCaseComponent }
      <AccessRequestModal getCollection={ getCollection } />
    </>
  );
};

export default ExploreSetsTable;
